import './App.css';
import { useEffect, useState } from 'react';

function App() {
  const [isAdminRoute, setIsAdminRoute] = useState(false);
  const [isUnavailableRoute, setIsUnavailableRoute] = useState(false);
  const [authed, setAuthed] = useState(false);
  const [noticeEnabled, setNoticeEnabled] = useState(false);
  const [noticeText, setNoticeText] = useState('');
  const [siteClosed, setSiteClosed] = useState(false);
  const [unavailEnabled, setUnavailEnabled] = useState(false);
  const [unavailText, setUnavailText] = useState('');

  useEffect(() => {
    const path = window.location.pathname.toLowerCase();
    setIsAdminRoute(path.startsWith('/admin'));
    setIsUnavailableRoute(path.startsWith('/unavailable'));
    setAuthed(localStorage.getItem('adminAuthed') === 'true');
    // Fetch notice from backend (Vercel KV)
    (async () => {
      try {
        const res = await fetch('/api/notice');
        if (res.ok) {
          const data = await res.json();
          setNoticeEnabled(!!data.enabled);
          setNoticeText(data.text || '');
          setSiteClosed(!!data.closed);
          setUnavailEnabled(!!data.unavailEnabled);
          setUnavailText(data.unavailText || '');
        } else {
          // fallback to any existing local value
          setNoticeEnabled(localStorage.getItem('noticeEnabled') === 'true');
          setNoticeText(localStorage.getItem('noticeText') || '');
          setSiteClosed(localStorage.getItem('siteClosed') === 'true');
          setUnavailEnabled(localStorage.getItem('unavailEnabled') === 'true');
          setUnavailText(localStorage.getItem('unavailText') || '');
        }
      } catch (_) {
        setNoticeEnabled(localStorage.getItem('noticeEnabled') === 'true');
        setNoticeText(localStorage.getItem('noticeText') || '');
        setSiteClosed(localStorage.getItem('siteClosed') === 'true');
        setUnavailEnabled(localStorage.getItem('unavailEnabled') === 'true');
        setUnavailText(localStorage.getItem('unavailText') || '');
      }
    })();
  }, []);

  // Update tab title based on route/state
  useEffect(() => {
    let page = 'Home';
    if (isAdminRoute) page = 'Admin';
    else if (isUnavailableRoute || siteClosed) page = 'Currently Unavailable';
    document.title = `Whangamata Taxis | ${page}`;
  }, [isAdminRoute, isUnavailableRoute, siteClosed]);

  // Redirect visitors to /unavailable when site is closed
  useEffect(() => {
    const path = window.location.pathname.toLowerCase();
    const isAdmin = path.startsWith('/admin');
    const isUnavail = path.startsWith('/unavailable');
    if (siteClosed && !isAdmin && !isUnavail) {
      window.history.replaceState(null, '', '/unavailable');
      setIsUnavailableRoute(true);
    }
  }, [siteClosed]);

  const handleLogin = (e) => {
    e.preventDefault();
    const form = new FormData(e.currentTarget);
    const password = form.get('password');
    (async () => {
      try {
        const res = await fetch('/api/login', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ password }),
          credentials: 'same-origin',
        });
        if (!res.ok) throw new Error('Invalid credentials');
        localStorage.setItem('adminAuthed', 'true');
        setAuthed(true);
      } catch (err) {
        alert('Incorrect password');
      }
    })();
  };

  const handleSaveNotice = async (e) => {
    e.preventDefault();
    try {
      const res = await fetch('/api/notice', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          enabled: noticeEnabled,
          text: noticeText,
          closed: siteClosed,
          unavailEnabled,
          unavailText,
        }),
        credentials: 'same-origin',
      });
      if (!res.ok) throw new Error('Request failed');
      alert('Saved');
    } catch (err) {
      // fallback store locally so you can continue testing
      localStorage.setItem('noticeEnabled', noticeEnabled ? 'true' : 'false');
      localStorage.setItem('noticeText', noticeText || '');
      localStorage.setItem('siteClosed', siteClosed ? 'true' : 'false');
      localStorage.setItem('unavailEnabled', unavailEnabled ? 'true' : 'false');
      localStorage.setItem('unavailText', unavailText || '');
      alert('Saved locally (backend unreachable).');
    }
  };

  const handleLogout = () => {
    (async () => {
      try {
        await fetch('/api/logout', { method: 'POST', credentials: 'same-origin' });
      } catch (_) {}
      localStorage.removeItem('adminAuthed');
      setAuthed(false);
    })();
  };

  // If site is closed, show Unavailable page for all non-admin routes
  if (!isAdminRoute && (isUnavailableRoute || siteClosed)) {
    return (
      <div className="App">
        <header className="Header">
          <div className="BookingRow">
            <h1 className="TaxiHeading">WHANGAMATA TAXIS</h1>
            <div className="BookingCard">
              <h2>Sorry, we're unavailable right now</h2>
              <p>{unavailEnabled && unavailText ? unavailText : "We are currently not accepting bookings. Please check back soon."}</p>
            </div>
          </div>
        </header>
      </div>
    );
  }

  // Admin route
  if (isAdminRoute) {
    return (
      <div className="App">
        <header className="Header">
          <div className="BookingRow">
            <h1 className="TaxiHeading">WHANGAMATA TAXIS</h1>
            <div className="BookingCard">
              <h2>Admin</h2>
              {!authed ? (
                <form onSubmit={handleLogin} className="AdminForm">
                  <label>
                    Password
                    <input type="password" name="password" placeholder="Enter password" />
                  </label>
                  <button type="submit" className="CallButton">Sign In</button>
                </form>
              ) : (
                <p>Use the settings below to manage the notice banner and site availability.</p>
              )}
            </div>
          </div>
        </header>

        {authed && (
          <main className="Content grid">
            <section className="Panel fullwidth">
              <h3>Site Settings</h3>
              <form onSubmit={handleSaveNotice} className="AdminForm">
                <label className="Row">
                  <input
                    type="checkbox"
                    checked={noticeEnabled}
                    onChange={(e) => setNoticeEnabled(e.target.checked)}
                  />
                  <span>Show notice banner on main page</span>
                </label>

                <label className="Block">
                  <span className="FieldLabel">Notice text</span>
                  <textarea
                    rows="4"
                    value={noticeText}
                    onChange={(e) => setNoticeText(e.target.value)}
                    placeholder="e.g., Limited availability this weekend"
                  />
                </label>

                <label className="Row">
                  <input
                    type="checkbox"
                    checked={siteClosed}
                    onChange={(e) => setSiteClosed(e.target.checked)}
                  />
                  <span>Temporarily close site (redirect visitors)</span>
                </label>

                <label className="Row">
                  <input
                    type="checkbox"
                    checked={unavailEnabled}
                    onChange={(e) => setUnavailEnabled(e.target.checked)}
                  />
                  <span>Enable custom Unavailable page message</span>
                </label>
                <label className="Block">
                  <span className="FieldLabel">Unavailable page message</span>
                  <textarea
                    rows="3"
                    value={unavailText}
                    onChange={(e) => setUnavailText(e.target.value)}
                    placeholder="e.g., We’re off the road tonight. Back tomorrow!"
                  />
                </label>
                <div className="Row">
                  <button type="submit" className="CallButton">Save</button>
                  <button type="button" className="CallButton" onClick={handleLogout}>Sign Out</button>
                </div>
              </form>
            </section>
          </main>
        )}
      </div>
    );
  }

  return (
    <div className="App">
      <header className="Header">

        <div className="BookingRow">
          <h1 className="TaxiHeading">WHANGAMATA TAXIS</h1>
          <div className="BookingCard">
            <h2>Book a Taxi (Call or Text)</h2>
            <a className="Phone" href="tel:0226892531" aria-label="Call 022 689 2531">022 689 2531</a>
            <div className="BookingNotes" style={{color: '#F5F5F5', fontWeight: '600', marginTop: '0.5rem'}}>Cash Discounts - Ask your driver!</div>
            <div className="BookingNotes" style={{color: '#F5F5F5', fontWeight: '600', marginTop: '0.5rem'}}>Up to 6 Seats in our Taxi Van!</div>
            <div>
              <a className="CallButton" href="tel:0226892531" aria-label="Call now 022 689 2531">Call Now</a>
            </div>
          </div>
        </div>
      </header>

      {noticeEnabled && noticeText ? (
        <div className="NoticeBanner" role="status" aria-live="polite">
          <span className="NoticeBannerText">{noticeText}</span>
        </div>
      ) : null}

      <main className="Content grid">
        <section className="Panel">
          <h3>Fares</h3>
          <div className="FareGroup">
            <h4>Across Town</h4>
            <ul>
              <li>
                <span>Whangamata Township</span>
                <span className="price">$15 + $5 per extra passenger</span>
              </li>
            </ul>
          </div>

          <div className="FareGroup">
            <h4>Out of Town</h4>
            <ul>
              <li>
                <span>Onemana</span>
              </li>
              <li>
                <span>Wentworth Valley</span>
              </li>
              <li>
                <span>Transfer Station</span>
                <span className="price">$22 + $5 per extra passenger</span>
              </li>
            </ul>
          </div>

          <div className="FareGroup">
            <h4>Way Out of Town</h4>
            <ul>
              <li>
                <span>Opoutere / Whiritoa</span>
                <span className="price">$38 + $6 per extra passenger</span>
              </li>
            </ul>
          </div>
        </section>

        <section className="Panel">
          <h3>Airport Runs</h3>
          <table className="Table">
            <tbody>
              <tr>
                <td>Auckland (International & Domestic)</td>
                <td className="price">$260 for two - $45 per extra passenger</td>
              </tr>
              <tr>
                <td>Hamilton</td>
                <td className="price">$195 for two - $35 per extra passenger</td>
              </tr>
              <tr>
                <td>Tauranga</td>
                <td className="price">$175 for two - $25 per extra passenger</td>
              </tr>
            </tbody>
          </table>

          <h3>Bus Links</h3>
          <table className="Table">
            <tbody>
              <tr>
                <td>Waihi</td>
                <td className="price">$65 per passenger</td>
              </tr>
              <tr>
                <td>Prescott's Garage</td>
                <td className="price">$45 per passenger</td>
              </tr>
            </tbody>
          </table>

          <h3>Specials</h3>
          <ul className="List">
            <li>
              <span>Hillbrook Estate</span>
              <span className="price">$20 per passenger</span>
            </li>
          </ul>
        </section>

        {/* Rules & Payment Section */}
        <section className="Panel fullwidth" aria-labelledby="rules-heading">
          <h3 id="rules-heading">Rules</h3>
          <ul className="List">
            <li>
              <span>No drinking, eating, smoking, or vaping inside the cab.</span>
            </li>
          </ul>

          <h3>Fees</h3>
          <ul className="List">
            <li>
              <span>Puke fee / cleaning fee</span>
              <span className="price">$250</span>
            </li>
          </ul>

          <h3>Payment</h3>
          <ul className="List">
            <li>
              <span>Cash or PayWave</span>
            </li>
            <li>
              <span>
                Note: Rides to Hillbrook Estate and Whiritoa may need to be paid
                in advance as internet can be sketchy.
              </span>
            </li>
          </ul>
        </section>
      </main>

      <footer className="Footer">
        © 2025 WHANGAMATA TAXIS
      </footer>
    </div>
  );
}

export default App;
